// =============================================================================
// RH › Pesquisa de Escala — relatório (distribuição das respostas)
// =============================================================================
// Renderizada embutida no hub RHForms.js.
//
// ⚠️ NÃO HÁ SCORE. A pesquisa é descritiva: não existe resposta "boa". O que se
// lê é a DISTRIBUIÇÃO de cada pergunta, com filtro por centro de custo.
//
// ⚠️⚠️ O CRUZAMENTO com centro de lucro NÃO EXISTE, e a ausência é deliberada:
// '06 | Técnico de TI' tem 3 pessoas na rede inteira e '13 | Conservação' tem 4 —
// cruzar função com centro de custo identificaria pessoas numa pesquisa que se
// apresenta como sem identificação individual. O centro de lucro aparece só no
// recorte de REDE, e mesmo assim como contagem de respostas, não como opinião.
// Não é "a gente evita fazer": o filtro não é oferecido.
//
// As perguntas vêm da API junto com os dados — a tela não tem cópia delas
// (fonte única: server/rh/pesquisa-escala-perguntas.js).

import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, Info, FileDown } from 'lucide-react';

const COR = '#e11d48';
const COR_FUNDO = '#fff1f2';

// Cores das opções. As duas escalas ganham cores próprias e estáveis (a mesma
// escala tem sempre a mesma cor em todas as perguntas); tudo o que é "indiferente
// / não sei / não se aplica" fica em cinza, porque não é uma terceira preferência.
// ⚠️ O 12x36 DEIXOU DE SER VERMELHO em 13/08 (alerta da Patrícia): o vermelho é a
// cor do "Não" logo abaixo, e quem batia o olho no gráfico lia a escala como um
// resultado ruim. A pesquisa é descritiva — nenhuma das duas escalas é a errada.
// ⚠️ Mesmas cores do PDF (server/rh/pdf-pesquisa-escala.js): mudou aqui, mude lá.
const CORES = {
    '12x36': '#F7613C',
    '5x2': '#0A8097',
    'Sim': '#059669',
    'Não': '#b91c1c',
    _neutro: '#94a3b8',
};
const corDaOpcao = (opcao) => CORES[opcao] || CORES._neutro;

// Os 8 recortes do PDF. ⚠️ Os `valor` são os mesmos do RECORTES no backend
// (server/rh/pesquisa-escala-agregacao.js) — divergir aqui faz o seletor pedir um
// recorte que o servidor não conhece, e ele devolve a rede inteira em silêncio.
// ⚠️ `arquivo` espelha o RECORTES do backend — é só o nome sugerido do download;
// quem manda é o Content-Disposition da resposta.
const RECORTES_PDF = [
    { valor: 'rede', rotulo: 'Rede completa', arquivo: 'rede' },
    { valor: 'MCDONALDS', rotulo: "Rede McDonald's", arquivo: 'rede-mcdonalds' },
    { valor: 'HUNGER', rotulo: 'HUNGER', arquivo: 'hunger' },
    { valor: 'P&G', rotulo: 'P&G', arquivo: 'p-e-g' },
    { valor: 'P&M', rotulo: 'P&M CORREIA', arquivo: 'p-e-m-correia' },
    { valor: 'GC FOOD', rotulo: 'GC FOOD', arquivo: 'gc-food' },
    { valor: 'P&M GALLI', rotulo: 'P&M GALLI', arquivo: 'p-e-m-galli' },
    { valor: 'PRESTADORAS', rotulo: 'Prestadoras de serviço', arquivo: 'prestadoras' },
];

function PesquisaEscalaRelatorio() {
    const [dados, setDados] = useState(null);
    const [centroCusto, setCentroCusto] = useState('');
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    const [baixando, setBaixando] = useState(null);   // guarda QUAL recorte está gerando
    const [recorte, setRecorte] = useState('rede');

    const carregar = useCallback(async (filtro) => {
        setCarregando(true);
        setErro(null);
        try {
            const url = '/api/rh/pesquisa-escala/respostas' + (filtro ? `?centro_custo=${encodeURIComponent(filtro)}` : '');
            const resposta = await fetch(url, {
                headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
            });
            if (!resposta.ok) throw new Error('Não foi possível carregar o relatório.');
            setDados(await resposta.json());
        } catch (e) {
            setErro(e.message);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { carregar(centroCusto); }, [carregar, centroCusto]);

    // Baixa um dos OITO PDFs: a rede inteira, a rede McDonald's (as 5 operadoras,
    // sem as prestadoras) e os 6 recortes por empresa — pedido do Sr. Marco em
    // 12/08: separar quem é empregado das operadoras de quem é das prestadoras.
    //
    // ⚠️ SÃO OITO DOCUMENTOS COM TOTAIS DIFERENTES para a mesma pesquisa. O recorte
    // vai no título, no cabeçalho e no NOME DO ARQUIVO do PDF — é o que impede que
    // um seja confundido com o outro depois de sair daqui por e-mail.
    //
    // ⚠️ A empresa é o CENTRO DE CUSTO (quem emprega), não a loja: o porteiro do
    // PTN que trabalha na ANV é prestadora, não HUNGER.
    //
    // ⚠️ Precisa passar pelo fetch (e não por um <a href>): a rota exige o token no
    // cabeçalho Authorization, e o navegador não o manda numa navegação comum.
    // Por isso o vaivém do blob + link temporário.
    //
    // ⚠️ O PDF NÃO segue o filtro de centro de custo da tela — ele é sempre da
    // rede inteira, e é assim de propósito: o documento reproduz o relatório de
    // quadro de funcionários, que é consolidado. Um PDF "de um centro de custo só"
    // teria recortes pequenos demais para divulgar.
    const baixarPdf = useCallback(async (recorte) => {
        setBaixando(recorte);
        setErro(null);
        try {
            const resposta = await fetch(
                `/api/rh/pesquisa-escala/relatorio-pdf?recorte=${encodeURIComponent(recorte)}`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
            );
            if (!resposta.ok) throw new Error('Não foi possível gerar o PDF.');

            const blob = await resposta.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const apelido = RECORTES_PDF.find((r) => r.valor === recorte)?.arquivo || 'rede';
            link.download = `pesquisa-escala-${apelido}-${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);   // sem isto o blob fica na memória da aba
        } catch (e) {
            setErro(e.message);
        } finally {
            setBaixando(null);
        }
    }, []);

    if (carregando && !dados) return <p style={{ color: '#64748b', padding: '20px' }}>Carregando…</p>;

    if (erro) {
        return (
            <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '20px', color: '#991b1b' }}>
                <AlertTriangle size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                {erro}
            </div>
        );
    }

    if (!dados?.existe) {
        return (
            <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', color: '#64748b' }}>
                Nenhuma pesquisa foi aberta ainda — não há respostas para mostrar.
            </div>
        );
    }

    // Agrupa a distribuição por pergunta: { P_1: [{ OPCAO, TOTAL }], … }
    const porPergunta = {};
    for (const linha of dados.distribuicao || []) {
        (porPergunta[linha.PERGUNTA] ||= []).push(linha);
    }

    const totalRespostas = Math.max(
        0,
        ...Object.values(porPergunta).map((linhas) => linhas.reduce((s, l) => s + Number(l.TOTAL), 0))
    );

    const cartao = {
        backgroundColor: '#fff', borderRadius: '16px', padding: '24px',
        border: '1px solid #f0e2e2', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Filtro + total */}
            <div style={{ ...cartao, display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>Centro de custo</label>
                    <select
                        value={centroCusto}
                        onChange={(e) => setCentroCusto(e.target.value)}
                        style={{
                            padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0',
                            fontSize: '13.5px', color: '#0f172a', backgroundColor: '#fff', minWidth: '200px',
                        }}
                    >
                        <option value="">Todos</option>
                        {(dados.centrosCusto || []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button
                        onClick={() => carregar(centroCusto)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px',
                            borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9',
                            color: '#475569', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                        }}
                    >
                        <RefreshCw size={15} /> Atualizar
                    </button>

                    {/* Os SETE PDFs — ver baixarPdf(). Seletor + um botão, para não
                        virar uma parede de botões. */}
                    <label style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginLeft: '4px' }}>
                        Relatório
                    </label>
                    <select
                        value={recorte}
                        onChange={(e) => setRecorte(e.target.value)}
                        disabled={!!baixando}
                        style={{
                            padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0',
                            fontSize: '13.5px', color: '#0f172a', backgroundColor: '#fff', minWidth: '190px',
                        }}
                    >
                        {RECORTES_PDF.map((r) => (
                            <option key={r.valor} value={r.valor}>{r.rotulo}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => baixarPdf(recorte)}
                        disabled={!!baixando}
                        title="Gera o PDF do recorte escolhido. A empresa é quem emprega (centro de custo), não a loja."
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px',
                            borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: 700,
                            backgroundColor: baixando ? '#fda4af' : COR, color: '#fff',
                            cursor: baixando ? 'default' : 'pointer',
                        }}
                    >
                        <FileDown size={15} /> {baixando ? 'Gerando…' : 'Baixar PDF'}
                    </button>
                </div>
                <div style={{ fontSize: '13px', color: '#64748b' }}>
                    <strong style={{ color: '#0f172a', fontSize: '18px', fontVariantNumeric: 'tabular-nums' }}>{totalRespostas}</strong> resposta(s)
                    {centroCusto && <> em <strong>{centroCusto}</strong></>}
                </div>
            </div>

            {/* Aviso: a pergunta 1 é auto-reportada. Sem isto, alguém trata o
                resultado como se fosse dado de cadastro do RH.
                A legenda das escalas vem junto: quem lê o relatório não viu o
                formulário, e "12x36" sozinho não diz o que a pessoa respondeu. */}
            <div style={{ backgroundColor: COR_FUNDO, border: '1px solid #fecdd3', borderRadius: '12px', padding: '14px 18px', fontSize: '13px', color: '#881337', lineHeight: 1.6 }}>
                <Info size={15} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                A escala informada na pergunta 1 é <strong>declarada pelo próprio colaborador</strong> — não vem do
                cadastro. Use como percepção, não como cadastro de jornada.
                {dados.escalas?.length > 0 && (
                    <span style={{ display: 'block', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #fecdd3' }}>
                        {dados.escalas.map((e, i) => (
                            <span key={e.nome}>
                                {i > 0 && ' · '}
                                <strong>{e.nome}</strong>: {e.explicacao}
                            </span>
                        ))}
                        {' '}— foi assim que apareceu para quem respondeu.
                    </span>
                )}
            </div>

            {/* Distribuição, pergunta a pergunta */}
            {(dados.perguntas || []).map((pergunta, indice) => {
                const linhas = porPergunta[pergunta.id] || [];
                const total = linhas.reduce((s, l) => s + Number(l.TOTAL), 0);

                return (
                    <div key={pergunta.id} style={cartao}>
                        <p style={{ fontSize: '15px', fontWeight: 600, color: '#0f172a', margin: '0 0 16px 0', lineHeight: 1.5 }}>
                            <span style={{ color: COR, marginRight: '6px' }}>{indice + 1}.</span>
                            {pergunta.texto}
                        </p>

                        {total === 0 ? (
                            <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>Sem respostas neste recorte.</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {/* Percorre as OPÇÕES do catálogo (não o resultado): assim a
                                    opção com zero resposta aparece como zero, em vez de sumir —
                                    "ninguém escolheu" é informação. */}
                                {pergunta.opcoes.map((opcao) => {
                                    const encontrada = linhas.find((l) => l.OPCAO === opcao);
                                    const quantidade = encontrada ? Number(encontrada.TOTAL) : 0;
                                    const pct = total > 0 ? (quantidade / total) * 100 : 0;
                                    const cor = corDaOpcao(opcao);
                                    return (
                                        <div key={opcao}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '5px' }}>
                                                <span style={{ color: '#334155', fontWeight: 600 }}>{opcao}</span>
                                                <span style={{ color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
                                                    {quantidade} · {pct.toFixed(1)}%
                                                </span>
                                            </div>
                                            <div style={{ backgroundColor: '#f1f5f9', borderRadius: '99px', height: '20px', overflow: 'hidden' }}>
                                                <div style={{
                                                    width: `${pct}%`, height: '100%', backgroundColor: cor,
                                                    borderRadius: '99px', transition: 'width 0.3s ease',
                                                }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Recorte de rede por centro de lucro — contagem de PARTICIPAÇÃO, não
                de opinião, e sem cruzar com centro de custo (ver cabeçalho). */}
            {dados.porCentroLucro?.length > 0 && (
                <div style={cartao}>
                    <h4 style={{ margin: '0 0 6px 0', fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
                        Participação por função (rede inteira)
                    </h4>
                    <p style={{ margin: '0 0 14px 0', fontSize: '12.5px', color: '#94a3b8', lineHeight: 1.6 }}>
                        Quantas respostas vieram de cada centro de lucro. Este recorte é sempre da rede toda e
                        não se combina com o filtro de centro de custo — grupos pequenos ficariam identificáveis.
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {dados.porCentroLucro.map((c) => (
                            <span key={c.NOME_CENTRO_LUCRO || 'sem'} style={{
                                backgroundColor: '#f1f5f9', color: '#475569', borderRadius: '99px',
                                padding: '5px 12px', fontSize: '12.5px', fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                            }}>
                                {c.NOME_CENTRO_LUCRO || 'Sem função'}: {c.TOTAL}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default PesquisaEscalaRelatorio;
